import { createFileRoute, Link } from '@tanstack/react-router'
import { CalendarDays, LineChart, Droplets, ArrowRight, Lock, KeyRound } from 'lucide-react'
import { HOME_FAQS_PUBLIC } from '@/lib/seo'

export const Route = createFileRoute('/_layout/')({
  component: HomePage,
})

const trust = [
  {
    icon: CalendarDays,
    title: 'Log & predict',
    text: 'Track periods, flow and symptoms — see your next period, fertile window and ovulation.',
  },
  {
    icon: Lock,
    title: 'Private by default',
    text: 'Your cycle data stays in your browser. No account, nothing uploaded unless you choose sync.',
  },
  {
    icon: KeyRound,
    title: 'Encrypted sync',
    text: 'Sign in to sync and back up across your devices — end-to-end encrypted, so even we can’t read it.',
  },
]

const features = [
  {
    icon: CalendarDays,
    title: 'Log periods & symptoms',
    text: 'Track flow, cramps, mood, sleep and more. Patterns emerge from your own history, not a template.',
  },
  {
    icon: Droplets,
    title: 'Predict your cycle',
    text: 'Next period, fertile window and ovulation estimates based on your averaged cycle length.',
  },
  {
    icon: LineChart,
    title: 'See the pattern',
    text: 'A clear calendar and timeline show where you are in your cycle today and what is coming.',
  },
]

const steps = [
  {
    n: '1',
    title: 'Open the tracker',
    text: 'No download. It loads in your browser — use it privately or sign in to sync.',
  },
  {
    n: '2',
    title: 'Log your period',
    text: 'Add the start date and flow. Add symptoms if you like.',
  },
  {
    n: '3',
    title: 'See predictions',
    text: 'Lunara estimates your next period and fertile window instantly.',
  },
]

function HomePage() {
  return (
    <div>
      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 pt-16 pb-12 sm:pt-24">
        <div className="mx-auto max-w-3xl text-center">
          <span className="border-border bg-surface-1 text-primary inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium">
            <Lock size={14} /> Private by default — optional encrypted sync
          </span>
          <h1 className="text-text-primary mt-6 text-4xl leading-tight font-bold tracking-tight sm:text-5xl">
            Track your period and understand your cycle
          </h1>
          <p className="text-text-secondary mx-auto mt-5 max-w-2xl text-lg">
            Lunara runs in your browser. Log your period, symptoms and predictions with no account —
            or sign in to sync securely across your devices, end-to-end encrypted.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/tracker"
              className="bg-primary text-on-primary hover:bg-primary-hover inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold"
            >
              Open the tracker <ArrowRight size={18} />
            </Link>
            <Link
              to="/about"
              className="border-border text-text-primary hover:bg-surface-2 inline-flex items-center gap-2 rounded-xl border px-6 py-3 font-semibold"
            >
              How it works
            </Link>
          </div>
          <p className="text-text-muted mt-4 text-sm">
            Free · Works offline · Private by default · Optional encrypted sync
          </p>
        </div>
      </section>

      {/* Trust row */}
      <section className="mx-auto max-w-6xl px-4 pb-8">
        <div className="grid gap-4 sm:grid-cols-3">
          {trust.map((t) => (
            <div key={t.title} className="border-border bg-surface-1 rounded-2xl border p-5">
              <t.icon className="text-primary" size={22} />
              <h3 className="text-text-primary mt-3 font-semibold">{t.title}</h3>
              <p className="text-text-secondary mt-1 text-sm">{t.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-4 py-16">
        <h2 className="text-text-primary text-center text-2xl font-bold">
          Everything for your cycle, in one place
        </h2>
        <p className="text-text-secondary mx-auto mt-3 max-w-2xl text-center">
          Built for awareness — not a medical device, not a contraceptive.
        </p>
        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {features.map((f) => (
            <div key={f.title} className="border-border bg-surface-1 rounded-2xl border p-6">
              <div className="bg-surface-2 text-primary inline-flex h-10 w-10 items-center justify-center rounded-xl">
                <f.icon size={20} />
              </div>
              <h3 className="text-text-primary mt-4 font-semibold">{f.title}</h3>
              <p className="text-text-secondary mt-1 text-sm">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-surface-2">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <h2 className="text-text-primary text-center text-2xl font-bold">Start in seconds</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {steps.map((s) => (
              <div key={s.n} className="text-center">
                <div className="bg-primary text-on-primary mx-auto inline-flex h-10 w-10 items-center justify-center rounded-full font-bold">
                  {s.n}
                </div>
                <h3 className="text-text-primary mt-4 font-semibold">{s.title}</h3>
                <p className="text-text-secondary mt-1 text-sm">{s.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/tracker"
              className="bg-primary text-on-primary hover:bg-primary-hover inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold"
            >
              Try it now <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ (matches FAQPage JSON-LD) */}
      <section className="mx-auto max-w-3xl px-4 py-16">
        <h2 className="text-text-primary text-center text-2xl font-bold">
          Frequently asked questions
        </h2>
        <div className="mt-8 space-y-4">
          {HOME_FAQS_PUBLIC.map((f) => (
            <div key={f.q} className="border-border bg-surface-1 rounded-2xl border p-5">
              <h3 className="text-text-primary font-semibold">{f.q}</h3>
              <p className="text-text-secondary mt-2 text-sm leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
